import type { AssetSheetProgress } from '@atlas/types'
import { CardShell, ErrorState, ListSkeleton } from '@atlas/ui-components'
import { Button, Tab, TabList, TabPanel, Tabs } from '@atlas/ui-primitives'
import { ArrowUpRight, RefreshCw } from 'lucide-react'
import { useEffect, useRef } from 'react'
import type { Key } from 'react-aria-components'
import { useSearchParams } from 'react-router-dom'
import { useRefreshRowsAfterSync, useStartAssetSheetSync } from '@/hooks/useAssetSheet'
import { apiErrorMessage } from '@/lib/api-error'
import { formatShortDateTime } from '@/lib/format-short-date-time'
import { notification } from '@/notifications/notification-store'
import { AssetSheetParam, AssetSheetView } from './asset-sheet-view'
import { ColumnMapTab } from './ColumnMapTab'
import { formatSize } from './format-size'
import { LastSyncSummary } from './LastSyncSummary'
import { OverviewTab } from './OverviewTab'
import { RowsTab } from './RowsTab'
import { useSyncProgress } from './useSyncProgress'

/**
 * ADMIN page for the asset sheet: see the SharePoint workbook and start a sync above three tabs:
 * the processes a sync sets off, every row with its status and why, and the column map.
 */
export function AssetSheetPage() {
	const { progress, isStarting, canStart, start, cancel } = useSyncProgress()
	const [searchParams, setSearchParams] = useSearchParams()
	const view = viewOf(searchParams.get(AssetSheetParam.VIEW))
	useRefreshRowsAfterSync(progress.data?.sync.lastSyncedAt ?? null)

	// Once when a loaded page's reads start failing, not on every failed poll; a first read that
	// fails is shown in place instead.
	const failing = progress.isRefetchError
	const error = useRef(progress.error)
	error.current = progress.error
	useEffect(() => {
		if (failing) notification.error(apiErrorMessage(error.current, 'The progress could not be read.'))
	}, [failing])

	// Each tab keeps only its own parameters; the workbook tab is shared by Rows and the column map.
	const selectView = (key: Key) => {
		const next = new URLSearchParams({ [AssetSheetParam.VIEW]: String(key) })
		const sheetTab = searchParams.get(AssetSheetParam.SHEET_TAB)
		if (sheetTab && key !== AssetSheetView.OVERVIEW) next.set(AssetSheetParam.SHEET_TAB, sheetTab)
		setSearchParams(next)
	}

	return (
		<div className="mx-auto flex w-full max-w-5xl flex-col gap-ds-6 p-ds-6">
			<header className="flex flex-col gap-ds-1">
				<h1 className="font-ds-display text-ds-2xl text-ds-text">Asset sheet sync</h1>
				<p className="max-w-2xl text-ds-sm text-ds-text-muted">
					Syncing reads the knowledge base workbook from SharePoint: each row is matched to the taxonomy, its Vault
					assets are downloaded, and the downloaded files are tagged. A sync also runs every night at 02:00 UTC.
				</p>
			</header>

			{progress.isPending ? (
				<ListSkeleton label="Loading progress" />
			) : !progress.data ? (
				<ErrorState
					title="Could not load the progress"
					description={apiErrorMessage(progress.error, 'Try again.')}
					action={
						<Button variant="secondary" size="sm" onPress={() => void progress.refetch()}>
							Try again
						</Button>
					}
				/>
			) : (
				<>
					<div className="flex flex-col gap-ds-3">
						<SheetActions progress={progress.data} canStart={canStart} onSyncRequested={start} onSyncRefused={cancel} />
						<LastSyncSummary running={progress.data.sync.running} lastSyncedAt={progress.data.sync.lastSyncedAt} />
					</div>
					<Tabs selectedKey={view} onSelectionChange={selectView}>
						<TabList variant="underline" className="mb-ds-4" aria-label="Asset sheet views">
							<Tab id={AssetSheetView.OVERVIEW}>Overview</Tab>
							<Tab id={AssetSheetView.ROWS}>Rows</Tab>
							<Tab id={AssetSheetView.COLUMN_MAP}>Column map</Tab>
						</TabList>
						<TabPanel id={AssetSheetView.OVERVIEW}>
							<OverviewTab progress={progress.data} isStarting={isStarting} />
						</TabPanel>
						<TabPanel id={AssetSheetView.ROWS}>
							<RowsTab />
						</TabPanel>
						<TabPanel id={AssetSheetView.COLUMN_MAP}>
							<ColumnMapTab />
						</TabPanel>
					</Tabs>
				</>
			)}
		</div>
	)
}

const VIEWS = new Set<string>(Object.values(AssetSheetView))
const viewOf = (value: string | null) => (value && VIEWS.has(value) ? value : AssetSheetView.OVERVIEW)

/** Who last edited the workbook the last sync read, and when. */
const describeSheet = ({ modifiedAt, modifiedBy, size }: NonNullable<AssetSheetProgress['sheet']>) =>
	[
		'Workbook last edited',
		modifiedBy && `by ${modifiedBy}`,
		modifiedAt && formatShortDateTime(modifiedAt),
		`(${formatSize(size)})`,
	]
		.filter(Boolean)
		.join(' ')

function SheetActions({
	progress,
	canStart,
	onSyncRequested,
	onSyncRefused,
}: {
	progress: AssetSheetProgress
	canStart: boolean
	onSyncRequested: () => void
	onSyncRefused: () => void
}) {
	const sync = useStartAssetSheetSync()
	const { sheet } = progress

	const startSync = () => {
		onSyncRequested()
		sync.mutate(undefined, {
			onSuccess: () => notification.info('Sync started. The progress below updates as it runs.'),
			onError: (error) => {
				onSyncRefused()
				notification.error(apiErrorMessage(error, 'The sync could not be started.'))
			},
		})
	}

	return (
		<CardShell
			as="section"
			aria-label="Workbook"
			className="flex flex-col gap-ds-3 sm:flex-row sm:items-center sm:justify-between"
		>
			<div className="flex flex-col gap-ds-1">
				<p className="text-ds-sm text-ds-text">
					{sheet ? describeSheet(sheet) : 'The workbook is read from SharePoint at the first sync.'}
				</p>
				{sheet?.webUrl && (
					<a
						href={sheet.webUrl}
						target="_blank"
						// `noopener` as well as `noreferrer`: SharePoint is another site, which must not
						// reach back through `window.opener`.
						rel="noopener noreferrer"
						className="inline-flex items-center gap-ds-1 self-start text-ds-sm text-ds-accent hover:underline"
					>
						<ArrowUpRight aria-hidden className="h-4 w-4" />
						Open in SharePoint
					</a>
				)}
			</div>
			<Button onPress={startSync} isDisabled={!canStart || progress.sync.running || sync.isPending}>
				<RefreshCw aria-hidden className="mr-ds-2 h-4 w-4" />
				Sync now
			</Button>
		</CardShell>
	)
}
